import { useMemo, useState } from "react";
import { PLUSH_TYPES, LOUD_COLORS, SIZES, savePrize } from "./prizes.js";
import { t } from "./i18n.js";
import { offerAvailable, claimReward } from "./rewards.js";
import PlushImg from "./PlushImg.jsx";

/* Overlay: spilleren vælger sit tøjdyr; størrelsen er givet af spillet.
   Farverne fordeles tilfældigt på dyrene ved hver visning.
   "Dobbelt op": én rewarded video kan opgradere størrelsen ÉT trin
   (lille→mellem→stor) — aldrig til pokal, og kun én gang pr. præmie. */
export default function PrizePicker({ size: sizeProp, game, onDone }) {
  const [size, setSize] = useState(sizeProp);
  const [adErr, setAdErr] = useState(false);
  const upgraded = size !== sizeProp;
  const next = size === "lille" ? "mellem" : size === "mellem" ? "stor" : null;

  const choices = useMemo(() => {
    const cols = [...LOUD_COLORS].sort(() => Math.random() - 0.5);
    return PLUSH_TYPES.map((t, i) => ({ t, c: cols[i % cols.length] }));
  }, []);

  const pick = (t, c) => {
    const prize = savePrize({ type: t.id, color: c.id, size, game });
    onDone(prize);
  };

  const doubleUp = () => {
    setAdErr(false);
    claimReward("dobbelt_op", {
      onReward: () => setSize(next),
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  return (
    <div className="pp-overlay">
      <style>{css}</style>
      <div className="pp-card">
        <div className="pp-ribbon">{t("pp_ribbon")}</div>
        <p className="pp-lede" dangerouslySetInnerHTML={{
          __html: t("pp_won", { size: `<b class="pp-size">${t("size_" + size).toUpperCase()}</b>` }),
        }} />
        {next && !upgraded && offerAvailable("dobbelt_op") && (
          <div className="pp-adrow">
            <span>{t("ad_offer_double", { size: t("size_" + next).toLowerCase() })}</span>
            <button className="pp-adbtn" onClick={doubleUp}>{t("ad_watch")}</button>
          </div>
        )}
        {adErr && <p className="pp-adfail">{t("ad_fail")}</p>}
        {upgraded && <p className="pp-upgraded">{t("ad_upgraded")}</p>}
        <div className="pp-grid">
          {choices.map(({ t: ty, c }) => (
            <button key={ty.id} className="pp-item" onClick={() => pick(ty, c)}>
              <PlushImg className="pp-emoji" type={ty.id} color={c.id} px={SIZES[size].pick} />
              <span className="pp-name">{t("color_" + c.id)} {t("type_" + ty.id).toLowerCase()}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const css = `
.pp-overlay{
  position:fixed; inset:0; background:rgba(10,20,8,.78); z-index:20;
  display:flex; align-items:center; justify-content:center; padding:12px;
}
.pp-card{
  width:min(96%,460px); max-height:88vh; overflow-y:auto;
  -webkit-overflow-scrolling:touch; overscroll-behavior:contain;
  background:#F6EED9; color:#22301c; border-radius:14px; padding:0 16px 18px;
  text-align:center; box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.pp-ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:#C8352E; color:#fff; margin:0 -16px 12px; padding:12px;
  border-radius:14px 14px 0 0;
}
.pp-lede{font-weight:800; font-size:15px; margin:4px 0 12px;}
.pp-size{background:#C8352E; color:#fff; padding:1px 8px; border-radius:4px; letter-spacing:.5px;}
.pp-adrow{
  display:flex; align-items:center; justify-content:center; gap:10px; flex-wrap:wrap;
  background:#fff7df; border:2px dashed #e0b23a; border-radius:10px;
  padding:8px 10px; margin:0 0 12px; font-size:13px; font-weight:800;
}
.pp-adbtn{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 12px; cursor:pointer;
  box-shadow:0 3px 0 #b07a14;
}
.pp-adbtn:active{transform:translateY(2px); box-shadow:0 1px 0 #b07a14;}
.pp-adfail{font-size:12px; font-weight:800; opacity:.7; margin:0 0 10px;}
.pp-upgraded{font-size:14px; font-weight:800; color:#a3720b; margin:0 0 12px;}
.pp-grid{display:grid; grid-template-columns:repeat(3,1fr); gap:8px;}
.pp-item{
  display:flex; flex-direction:column; align-items:center; gap:4px;
  background:#fff; border:2px solid #d8cfae; border-radius:10px;
  padding:10px 4px 8px; cursor:pointer; font-family:inherit;
  touch-action:pan-y;   /* let the card scroll vertically even when the touch starts on a tile */
}
.pp-item:hover{border-color:#C8352E; transform:translateY(-2px);}
.pp-emoji{line-height:1;}
.pp-name{font-size:10.5px; font-weight:800; color:#22301c;}
@media (max-width:380px){ .pp-grid{grid-template-columns:repeat(2,1fr);} }
`;
